import React, { useState, useEffect } from "react";
import numeral from "numeral";
import {
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import ApiCaller from "../../../../api/apiCaller";
import "./index.css";
import t from "utils/Lang";

const StoreProfitTypes = [
  {
    type: "high_profit",
    description: "EBITDA tháng trước >= 130M hoặc %EBITDA >= 30%",
  },
  {
    type: "medium_profit",
    description: "EBITDA tháng trước 90M-130M hoặc %EBITDA 15-30%",
  },
  { type: "low_profit", description: "EBITDA tháng trước < 90M" },
  { type: "loss", description: "EBITDA < 0" },
];

interface SiteCannibalizationModalProps {
  siteId: number;
  isView?: boolean;
  isShow?: boolean;
  onSaveCallback?: Function;
  onCancelCallback?: Function;
  toggle: () => void;
}

export default function SiteCannibalizationModal({
  siteId,
  isView,
  isShow,
  onSaveCallback,
  onCancelCallback,
  toggle,
}: SiteCannibalizationModalProps) {
  const [currentUser, setCurrentUser] = useState<any>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<String>("");
  const [cannibalizationResult, setCannibalizationResult] = useState<
    Array<any>
  >([]);
  const getCurrentUser = async () => {
    const res = await ApiCaller.get("userme/me");
    if (res.result) {
      setCurrentUser(res.result);
    }
  };

  const getCalculationResult = async () => {
    setIsLoading(true);
    const uri = `siteaicannprojectionmodelapi/estimate/${siteId}`;
    const res = await ApiCaller.post(uri);
    console.log("res", res);
    if (res?.result) {
      console.log(res?.result?.data);
      if (res.result.data && res.result.data.length > 0) {
        setCannibalizationResult(res?.result?.data);
        setIsLoading(false);
      } else {
        setErrorMessage("Không có cửa hàng nào trong bán kính 2km");
        setIsLoading(false);
      }
    } else {
      setErrorMessage(res.msg || res.message);
      setIsLoading(false);
    }
  };

  const cancelModal = () => {
    toggle();
    if (onCancelCallback) {
      onCancelCallback();
    }
  };

  useEffect(() => {
    getCalculationResult();
    getCurrentUser();
  }, []);

  return (
    <Modal size="lg" isOpen={isShow} toggle={toggle}>
      <ModalHeader toggle={toggle}>
        {t().site_cannibalization_modal__title}
      </ModalHeader>
      <ModalBody>
        {errorMessage && (
          <Row>
            <Col xs={12}>
              <p style={styles.error}>{errorMessage}</p>
            </Col>
          </Row>
        )}
        <Row>
          <Col xs={12}>
            <div style={styles.legend}>
              {StoreProfitTypes.map((StoreProfitType, index) => (
                <div className="cannibalization-modal__store_profit_legend_line" key={index}>
                  <span
                    className={`cannibalization-modal__store_profit_legend ${StoreProfitType.type}`}
                  >
                    {StoreProfitType.type}
                  </span>
                  <span className="cannibalization-modal__store_profit_legend_description">
                    {StoreProfitType.description}
                  </span>
                </div>
              ))}
            </div>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <span style={styles.tips}>
              <b>{t().site_cannibalization_modal__cannibalization_rate}</b>{" "}
              {t().site_cannibalization_modal__cannibalization_rate_description}
            </span>
          </Col>
        </Row>
        {isLoading && (
          <Row>
            <Col xs={12}>
              <p style={styles.loading}>
                {t().site_cannibalization_modal__calculating}
              </p>
            </Col>
          </Row>
        )}
        <Row>
          {cannibalizationResult.map((cannibalizationStore: any, index) => (
            <Col xs={12} key={index}>
              <div style={styles.resultCard}>
                <div style={styles.storeName}>
                  <a
                    href={`/store/${cannibalizationStore.store_id}`}
                    target="_blank" rel="noreferrer"
                  >
                    {cannibalizationStore.store_name}
                  </a>
                  <span
                    className={`cannibalization-modal__store_profit_type ${cannibalizationStore.store_profit_type}`}
                  >
                    {cannibalizationStore.store_profit_type}
                  </span>
                </div>
                <div>
                  {t().site_cannibalization_modal__distance}:{" "}
                  <span style={styles.resultHighlight}>
                    {numeral(cannibalizationStore.distance_km).format("0,0.00")}{" "}
                    km
                  </span>
                </div>
                <div>
                  {t().site_cannibalization_modal__cannibalization_rate}:{" "}
                  <span style={styles.resultHighlight}>
                    {numeral(
                      cannibalizationStore.cannibalization_rate * 100
                    ).format("0.00")}
                    %
                  </span>
                </div>
                <div>
                  {t().site_cannibalization_modal__revenue_lost}:{" "}
                  <span style={styles.resultHighlight}>
                    {numeral(
                      cannibalizationStore.net_revenue_loss_from_cannibalization
                    ).format("0,0")}{" "}
                    {t().site_cannibalization_modal__vnd_per_month}
                  </span>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={cancelModal}>
          {t().action__cancel}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

const styles = {
  tips: {
    fontSize: 14,
    color: "var(--color-muted)",
  },
  legend: {
    marginBottom: 12,
    padding: 12,
    border: "1px solid var(--color-line)",
    borderRadius: 6,
  },
  loading: {
    marginTop: 24,
    textAlign: "center",
  } as React.CSSProperties,
  error: {
    fontSize: 16,
    color: "var(--danger)",
  },
  resultCard: {
    paddingTop: 12,
    paddingBottom: 12,
    borderBottom: "1px solid var(--color-line)",
  },
  storeName: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 12,
    color: "var(--color-accent-dark)",
  },
  resultHighlight: {
    fontWeight: 500,
  },
};
